import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { tokensAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Coins, Check, Loader2, Zap, Terminal } from 'lucide-react';
import { toast } from 'sonner';

export default function TokenPurchasePage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    tokensAPI.packages().then(res => { setPackages(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  // Check for return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error('Payment status check timed out');
      setPolling(false);
      return;
    }
    setPolling(true);
    try {
      const res = await tokensAPI.status(sessionId);
      if (res.data.payment_status === 'paid') {
        toast.success(`${res.data.tokens} tokens added!`);
        await refreshUser();
        setPolling(false);
        navigate('/tokens', { replace: true });
      } else if (res.data.status === 'expired') {
        toast.error('Payment expired');
        setPolling(false);
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handlePurchase = async (packageId) => {
    setPurchasing(packageId);
    try {
      const res = await tokensAPI.checkout({ package_id: packageId });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
      setPurchasing(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="tokens-page">
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white/50 hover:text-white" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
            </Button>
          </div>
          <div className="flex items-center gap-2 bg-white/5 rounded-md px-3 py-1.5 border border-white/10">
            <Coins className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-medium text-white" data-testid="current-balance">{user?.token_balance || 0}</span>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-10">
        {polling && (
          <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-4 mb-8 flex items-center gap-3" data-testid="payment-polling">
            <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
            <p className="text-sm text-indigo-400">Verifying your payment...</p>
          </div>
        )}

        <div className="text-center mb-12">
          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 mb-4 px-3 py-1">
            <Coins className="w-3 h-3 mr-1.5" /> Token Store
          </Badge>
          <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-3" style={{fontFamily: 'Space Grotesk, sans-serif'}} data-testid="tokens-title">
            Buy Tokens
          </h1>
          <p className="text-white/40 text-lg">Power your AI code generation</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packages.map((pkg, i) => (
            <Card key={pkg.id} className={`bg-white/[0.03] border transition-all hover:-translate-y-1 ${i === 1 ? 'border-indigo-500/50 shadow-[0_0_30px_rgba(99,102,241,0.15)]' : 'border-white/10 hover:border-white/20'}`} data-testid={`package-${pkg.id}`}>
              <CardHeader className="text-center pb-2">
                {i === 1 && <Badge className="bg-indigo-500/10 text-indigo-400 border-indigo-500/20 w-fit mx-auto mb-2 text-[10px]">Most Popular</Badge>}
                <CardTitle className="text-2xl text-white">{pkg.name}</CardTitle>
                <CardDescription className="text-white/40">{pkg.description}</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <div className="mb-4">
                  <span className="text-4xl font-bold text-white">${pkg.price}</span>
                </div>
                <div className="flex items-center justify-center gap-2 mb-6">
                  <Zap className="w-5 h-5 text-amber-400" />
                  <span className="text-lg font-semibold text-amber-400">{pkg.tokens.toLocaleString()} tokens</span>
                </div>
                <ul className="text-sm text-white/40 space-y-2 mb-6 text-left">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> AI code generation</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Full SDLC support</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Multiple LLM models</li>
                  {i >= 1 && <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Priority support</li>}
                  {i >= 2 && <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Team features</li>}
                </ul>
                <Button
                  className={`w-full ${i === 1 ? 'bg-indigo-600 hover:bg-indigo-500 shadow-[0_0_20px_rgba(99,102,241,0.3)]' : 'bg-white/5 hover:bg-white/10 border border-white/10'} text-white`}
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={purchasing === pkg.id}
                  data-testid={`buy-${pkg.id}`}
                >
                  {purchasing === pkg.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {purchasing === pkg.id ? 'Processing...' : 'Buy Now'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
